import { ProgramNames } from '../../../models/programs/Programs'
import { PROVIDER_TYPES } from '../../../models/providers/Providers'
import { PROGRAM_TAXONOMY, ProgramCustomerPropertyId } from '../../common/constants/customerProgram'
import { TREATMENT_OPTIONS } from '../../common/constants/treatmentOptions'
import { ConsentText, ProgramDisplayLabels } from '../messages'
import { ProgramConfig } from '../types'

export const BLENDED_CARE_THERAPY_PROGRAM = {
  programName: ProgramNames.BlendedCareTherapy,
  blendedCareProgramId: 'c8cb4da4-8957-4cff-9d8e-1fa742f06771',
  customerPropertyId: ProgramCustomerPropertyId.considerLyraTherapy,

  displayLabels: {
    default: ProgramDisplayLabels.therapy,
    sessionSummary: 'therapy',
  },

  associatedProviderTypes: [PROVIDER_TYPES.LYRATHERAPIST.lyraType, PROVIDER_TYPES.LYRAFELLOW.lyraType],
  associatedProviderModalities: ['lyraTherapy'],

  canRescheduleAppointment: true,
  selfPayEligible: true,

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.INDIVIDUAL_THERAPY],
    programTaxonomyTreatmentOptions: [
      {
        clientele: PROGRAM_TAXONOMY.clientele.individual,
        treatment: PROGRAM_TAXONOMY.treatment.therapy,
        partner: PROGRAM_TAXONOMY.partner.directAccess,
        offering: PROGRAM_TAXONOMY.offering.default,
      },
      {
        clientele: PROGRAM_TAXONOMY.clientele.individual,
        treatment: PROGRAM_TAXONOMY.treatment.therapy,
        partner: PROGRAM_TAXONOMY.partner.blendedCare,
        offering: PROGRAM_TAXONOMY.offering.default,
      },
      {
        clientele: PROGRAM_TAXONOMY.clientele.individual,
        treatment: PROGRAM_TAXONOMY.treatment.therapy,
        partner: PROGRAM_TAXONOMY.partner.blendedCare,
        offering: PROGRAM_TAXONOMY.offering.fellowship,
      },
    ],
    isFullTreatmentOption: false,
  },

  consents: [
    {
      consentText: ConsentText.therapyConsent,
      consentUrl: 'https://www.lyrahealth.com/lt-agreement',
      showConsentIf: () => true,
    },
  ],

  questionIdentifiers: {
    SI_QUESTION: 'phq9_9',
    SI_PLAN_QUESTION: 'phq9_9_1',
    SI_INTENT_QUESTION: 'phq9_9_2',
    CHECK_IN_SI_QUESTION: 'suicideIdeation',
    CHECK_IN_SELF_HARM_QUESTION: 'selfHarm',
    CHRT_SI_QUESTION: 'chrt_10',
    CHRT_SI_QUESTION_2: 'chrt_11',
    CHRT_SI_PLAN_QUESTION: 'chrt_12',
  },

  noteContentMetadataName: {
    INITIAL_NOTE: 'bctInitialNote',
    SESSION_NOTE: 'bctSessionNote',
    GENERAL_NOTE: 'bctGeneralNote',
    REFERRAL_NOTE: 'baseReferralNote',
    OLD_INITIAL_NOTE: 'initialNote',
    OLD_SESSION_NOTE: 'sessionNote',
    OLD_GENERAL_NOTE: 'generalNote',
    OLD_REFERRAL_NOTE: 'referralNote',
  },
} satisfies ProgramConfig

export const TEENS_THERAPY_PROGRAM = {
  programName: ProgramNames.TeensTherapy,
  blendedCareProgramId: 'b42a0c74-76c0-48c4-8a7e-a767418bd753',
  customerPropertyId: ProgramCustomerPropertyId.considerLyraTherapy,

  displayLabels: {
    default: ProgramDisplayLabels.teensTherapy,
    careType: 'Therapy',
    clientProfile: 'Teens',
    sessionSummary: 'therapy',
  },

  associatedProviderTypes: [PROVIDER_TYPES.LYRATEENSTHERAPIST.lyraType],
  associatedProviderModalities: ['lyraTherapy'],

  canRescheduleAppointment: true,
  selfPayEligible: true,

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.INDIVIDUAL_THERAPY],
    programTaxonomyTreatmentOptions: [
      {
        clientele: PROGRAM_TAXONOMY.clientele.individual,
        treatment: PROGRAM_TAXONOMY.treatment.therapy,
        partner: PROGRAM_TAXONOMY.partner.blendedCare,
        offering: PROGRAM_TAXONOMY.offering.default,
      },
    ],
    isFullTreatmentOption: false,
  },

  consents: [
    {
      consentText: ConsentText.therapyConsent,
      consentUrl: 'https://www.lyrahealth.com/lt-teen-agreement/',
      showConsentIf: () => true,
    },
  ],

  questionIdentifiers: {
    SI_QUESTION: 'phq9_9',
    SI_PLAN_QUESTION: 'phq9_9_1',
    SI_INTENT_QUESTION: 'phq9_9_2',
    CHECK_IN_SI_QUESTION: 'suicideIdeation',
    CHECK_IN_SELF_HARM_QUESTION: 'selfHarm',
    CHRT_SI_QUESTION: 'chrt_10',
    CHRT_SI_QUESTION_2: 'chrt_11',
    CHRT_SI_PLAN_QUESTION: 'chrt_12',
    THOUGHTS_ABOUT_DEATH_QUESTION: 'rcads25_18',
    CLIENT_INTAKE_ATTEMPTED_SUICIDE_QUESTION: 'suicide',
    CLIENT_INTAKE_SELF_INJURY_QUESTION: 'selfInjury',
  },

  noteContentMetadataName: {
    INITIAL_NOTE: 'lcttInitialNote',
    SESSION_NOTE: 'lcttSessionNote',
    GENERAL_NOTE: 'lcttGeneralNote',
    REFERRAL_NOTE: 'baseReferralNote',
  },
} satisfies ProgramConfig

export const DIALECTICAL_BEHAVIOR_THERAPY_PROGRAM = {
  programName: ProgramNames.DialecticalBehaviorTherapy,
  blendedCareProgramId: '5bd8fc86-836b-4610-84e5-b11f59fa48df',
  customerPropertyId: ProgramCustomerPropertyId.dialecticalBehaviorTherapy,

  displayLabels: {
    default: ProgramDisplayLabels.dialecticalBehaviorTherapy,
    careType: 'Dialectical Behavior Therapy',
    short: 'DBT',
    clientProfile: 'DBT',
    sessionSummary: 'DBT',
  },

  associatedProviderTypes: [PROVIDER_TYPES.LYRATHERAPIST.lyraType],
  associatedProviderModalities: ['lyraTherapy'],

  canRescheduleAppointment: true,
  selfPayEligible: true,

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.INDIVIDUAL_THERAPY],
    programTaxonomyTreatmentOptions: [
      {
        clientele: PROGRAM_TAXONOMY.clientele.individual,
        treatment: PROGRAM_TAXONOMY.treatment.therapy,
        partner: PROGRAM_TAXONOMY.partner.blendedCare,
        offering: PROGRAM_TAXONOMY.offering.dbt,
      },
    ],
    isFullTreatmentOption: false,
  },

  consents: [
    {
      consentText: ConsentText.dialecticalBehaviorTherapyConsent,
      consentUrl: 'https://www.lyrahealth.com/dbt-agreement/',
      showConsentIf: () => true,
    },
  ],

  questionIdentifiers: {
    SI_QUESTION: 'phq9_9',
    SI_PLAN_QUESTION: 'phq9_9_1',
    SI_INTENT_QUESTION: 'phq9_9_2',
    CHECK_IN_SI_QUESTION: 'suicideIdeation',
    CHECK_IN_SELF_HARM_QUESTION: 'selfHarm',
    CHRT_SI_QUESTION: 'chrt_10',
    CHRT_SI_QUESTION_2: 'chrt_11',
    CHRT_SI_PLAN_QUESTION: 'chrt_12',
  },

  noteContentMetadataName: {
    INITIAL_NOTE: 'bctInitialNote',
    SESSION_NOTE: 'dbtSessionNote',
    GENERAL_NOTE: 'dbtGeneralNote',
    REFERRAL_NOTE: 'baseReferralNote',
  },
} satisfies ProgramConfig
